import React, { useEffect, useState, useContext } from "react";
import {
  Paper, Typography, Box, TextField, MenuItem, CircularProgress,
  Pagination, Checkbox, Button, Chip, Tooltip, Divider
} from "@mui/material";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../functions/apiService";
import AuthContext from "../../context/AuthContext";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import FilterListIcon from "@mui/icons-material/FilterList";

const BRAND = "#BF1D2D";

const Notifications = () => {
  const [listType, setListType] = useState("All");
  const [loading, setLoading] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("0");
  const { pages } = useContext(AuthContext);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [notificationAll, setNotificationAll] = useState(0);
  const [notificationUnread, setNotificationUnread] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadNotifications = () => {
    setLoading(true);
    setSelectedIds([]);
    ApiService.get(`/get-notification-listing?page=${currentPage}&Notification_Group_Id=${selectedCategory}&Type=${listType}`)
      .then(({ data }) => {
        const notifications = data?.data?.notifications?.data || [];
        setNotificationAll(data?.data?.notifications?.total);
        setNotificationUnread(data?.data?.unReadCount);
        setTotalPages(Math.ceil(data?.data?.notifications?.total / data?.data?.notifications?.per_page));
        setNotificationData(notifications);
        setLoading(false);
        // Deleting the last rows on the final page leaves us past the end — step back.
        if (notifications.length === 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    loadNotifications();
  }, [selectedCategory, listType, currentPage]);

  const formatNotificationTime = (createTime) => {
    const created = moment(createTime);
    if (moment().isSame(created, "day")) return `Today at ${created.format("hh:mm A")}`;
    if (moment().subtract(1, "day").isSame(created, "day")) return `Yesterday at ${created.format("hh:mm A")}`;
    return created.format("MM/DD/YYYY · hh:mm A");
  };

  const getTypeMeta = (groupId) => {
    if (groupId == 1) {
      return { icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20 }} />, color: "#7a5af8", bg: "#f4f0ff" };
    }
    if (groupId == 6) {
      return { icon: <LocalFireDepartmentOutlinedIcon sx={{ fontSize: 20 }} />, color: BRAND, bg: "#fdecec" };
    }
    return { icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />, color: "#0e9384", bg: "#effdfa" };
  };

  const ReadNotifications = (item) => {
    const created = moment(item.Create_Time);
    ApiService.get(`/readNotification/${item.Notification_Id}`)
      .then(() => {
        if (item.Notification_Group_Id == 1 && pages.includes('V3_CERTIFICATE_MODULE')) {
          navigate(`/firehouse/certificate-members/${item?.Certificate_id}`);
        }
        else if (item.Notification_Group_Id == 6 && pages.includes('V3_NEW_FILL_STRUCTURE')) {
          navigate(`/firehouse/my-shift-calls`);
        }
        else {
          navigate(`/firehouse/schedule?date=${created.format('YYYY-MM-DD')}`);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setDeleteLoading(true);
    ApiService.post('/deleteNotifications', { ids: selectedIds })
      .then(() => {
        toast.success(`${selectedIds.length} notification(s) deleted`);
        window.dispatchEvent(new CustomEvent('notifications-deleted'));
        setDeleteLoading(false);
        loadNotifications();
      })
      .catch((err) => {
        console.error('deleteNotifications error:', err);
        toast.error('Failed to delete notifications');
        setDeleteLoading(false);
      });
  };

  // Clears only what the active filters match, so the payload mirrors the listing call.
  const handleClearAll = () => {
    setDeleteLoading(true);
    ApiService.post('/clearAllNotifications', {
      Notification_Group_Id: selectedCategory,
      Type: listType,
    })
      .then(() => {
        toast.success('All notifications cleared');
        window.dispatchEvent(new CustomEvent('notifications-deleted'));
        setDeleteLoading(false);
        loadNotifications();
      })
      .catch((err) => {
        console.error('clearAllNotifications error:', err);
        toast.error('Failed to clear notifications');
        setDeleteLoading(false);
      });
  };

  const allPageIds = notificationData?.map((n) => n.Notification_Id) || [];
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.includes(id));
  const someSelected = allPageIds.some((id) => selectedIds.includes(id));

  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : allPageIds);

  const toggleSelectOne = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const TABS = [
    { key: "All", label: "All", count: notificationAll },
    { key: "Unread", label: "Unread", count: notificationUnread },
  ];

  return (
    <Box className="font-fam" sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* ===== Hero header ===== */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "18px",
          px: { xs: 3, md: 4 },
          py: { xs: 2.5, md: 3 },
          color: "#fff",
          background: `
            radial-gradient(120% 140% at 88% 10%, rgba(216,53,72,0.55) 0%, rgba(216,53,72,0) 45%),
            linear-gradient(115deg, #4f0d15 0%, #851520 48%, #a8202e 100%)
          `,
          boxShadow: "0 12px 30px rgba(124,18,30,0.22)",
          display: "flex",
          alignItems: "center",
          gap: 2.25,
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(105deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 38%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", inset: 0, opacity: 0.5, backgroundImage: "radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)", backgroundSize: "16px 16px", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", top: -70, right: -30, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.07)", filter: "blur(8px)", pointerEvents: "none" }} />
        <Box
          sx={{
            flexShrink: 0, width: 54, height: 54, borderRadius: "15px",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(160deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.12) 100%)",
            border: "1px solid rgba(255,255,255,0.3)",
            boxShadow: "inset 0 1px 1px rgba(255,255,255,0.25)",
            backdropFilter: "blur(4px)", zIndex: 1,
          }}
        >
          <NotificationsActiveOutlinedIcon sx={{ fontSize: 28, color: "#fff" }} />
        </Box>
        <Box sx={{ zIndex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.4px", textShadow: "0 1px 3px rgba(0,0,0,0.18)" }}>
            Notifications
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mt: 0.5 }}>
            {notificationUnread > 0
              ? `You have ${notificationUnread} unread notification${notificationUnread > 1 ? "s" : ""}`
              : "You are all caught up"}
          </Typography>
        </Box>
      </Box>

      {/* ===== Main card ===== */}
      <Paper
        elevation={0}
        sx={{ mt: 2.5, border: "1px solid #ececf1", borderRadius: "16px", overflow: "hidden", backgroundColor: "#fff" }}
      >
        {/* Control bar: tabs + category */}
        <Box
          sx={{
            px: { xs: 2, md: 3 }, py: 2,
            borderBottom: "1px solid #f1f1f5",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 1.5,
            background: "linear-gradient(180deg, #fcfcfd 0%, #f8f8fa 100%)",
          }}
        >
          {/* Segmented tabs */}
          <Box sx={{ display: "flex", gap: 0.5, p: 0.5, borderRadius: "24px", backgroundColor: "#f0f0f3" }}>
            {TABS.map((tab) => {
              const active = listType === tab.key;
              return (
                <Box
                  key={tab.key}
                  onClick={() => { setCurrentPage(1); setListType(tab.key); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    px: 2.25, py: 0.7, borderRadius: "20px", cursor: "pointer",
                    fontSize: "13.5px", fontWeight: 600, userSelect: "none",
                    transition: "all 0.2s ease",
                    backgroundColor: active ? "#fff" : "transparent",
                    color: active ? BRAND : "#667085",
                    boxShadow: active ? "0 1px 3px rgba(16,24,40,0.12)" : "none",
                    "&:hover": { color: active ? BRAND : "#101828" },
                  }}
                >
                  {tab.label}
                  <Box
                    component="span"
                    sx={{
                      minWidth: 20, height: 20, px: 0.75, borderRadius: "10px",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: "11.5px", fontWeight: 700,
                      backgroundColor: active ? "#fdecec" : "#e4e4ea",
                      color: active ? BRAND : "#667085",
                    }}
                  >
                    {tab.count ?? 0}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Category filter */}
          <TextField
            select size="small"
            value={selectedCategory}
            onChange={(e) => { setCurrentPage(1); setSelectedCategory(e.target.value); }}
            InputProps={{
              startAdornment: <FilterListIcon sx={{ fontSize: 18, color: "#98a2b3", mr: 0.75 }} />,
              sx: { height: 42, fontSize: "14px", borderRadius: "10px", backgroundColor: "#fff", minWidth: 190 },
            }}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e4ec" },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cdcdd6" },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
            }}
          >
            <MenuItem value={"0"} className="font-fam">All Categories</MenuItem>
            <MenuItem value={"1"} className="font-fam">Certificates</MenuItem>
            <MenuItem value={"2"} className="font-fam">Schedule</MenuItem>
          </TextField>
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          {/* Loading state */}
          {loading && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 420, gap: 1.5 }}>
              <CircularProgress size={36} sx={{ color: BRAND }} />
              <Typography variant="caption" sx={{ color: "#98a2b3", fontWeight: 500 }}>Loading notifications…</Typography>
            </Box>
          )}

          {/* Empty state */}
          {!loading && notificationData?.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 1.5 }}>
              <Box sx={{ width: 76, height: 76, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f6f6f8", color: "#c4c4cf" }}>
                <NotificationsNoneOutlinedIcon sx={{ fontSize: 38 }} />
              </Box>
              <Typography variant="body1" sx={{ color: "#475467", fontWeight: 600 }}>
                {listType === "Unread" ? "No unread notifications" : "No notifications yet"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#98a2b3" }}>
                {listType === "Unread" ? "You are all caught up." : "New notifications will appear here."}
              </Typography>
            </Box>
          )}

          {!loading && notificationData?.length > 0 && (
            <>
              {/* Bulk action bar */}
              <Box
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  flexWrap: "wrap", gap: 1, mb: 2, px: 1, py: 1,
                  borderRadius: "10px", backgroundColor: "#fafafb", border: "1px solid #f0f0f3",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleSelectAll}
                    size="small"
                    sx={{ color: "#c4c4cf", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: BRAND } }}
                  />
                  <Typography sx={{ fontSize: "13.5px", color: "#475467", fontWeight: 500 }}>
                    {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {selectedIds.length > 0 && (
                    <Button
                      variant="outlined" size="small" disabled={deleteLoading}
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleDeleteSelected}
                      sx={{ borderColor: BRAND, color: BRAND, fontSize: "13px", textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: "#9a1624", backgroundColor: "rgba(191,29,45,0.05)" } }}
                    >
                      {deleteLoading ? "Deleting…" : `Delete (${selectedIds.length})`}
                    </Button>
                  )}
                  <Button
                    variant="outlined" size="small" disabled={deleteLoading}
                    startIcon={<ClearAllIcon />}
                    onClick={handleClearAll}
                    sx={{ borderColor: "#d5d5dd", color: "#667085", fontSize: "13px", textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: "#b0b0bb", backgroundColor: "rgba(0,0,0,0.03)" } }}
                  >
                    {deleteLoading ? "Clearing…" : "Clear All"}
                  </Button>
                </Box>
              </Box>

              {/* Notification list */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {notificationData.map((item) => {
                  const unread = item.Is_Read === 0;
                  const meta = getTypeMeta(item.Notification_Group_Id);
                  const selected = selectedIds.includes(item.Notification_Id);
                  return (
                    <Box
                      key={item.Notification_Id}
                      sx={{
                        position: "relative",
                        display: "flex", alignItems: "center", gap: 1.5,
                        pl: 2, pr: 1.5, py: 1.5,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: unread ? "rgba(191,29,45,0.22)" : "#ececf1",
                        backgroundColor: unread ? "rgba(191,29,45,0.035)" : "#fff",
                        transition: "box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease",
                        "&::before": unread ? {
                          content: '""', position: "absolute", top: 8, bottom: 8, left: 0,
                          width: "3px", borderRadius: "0 3px 3px 0", backgroundColor: BRAND,
                        } : {},
                        "&:hover": { boxShadow: "0 6px 18px rgba(16,24,40,0.08)", transform: "translateY(-1px)", borderColor: unread ? "rgba(191,29,45,0.35)" : "#e0e0e8" },
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        onChange={() => toggleSelectOne(item.Notification_Id)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ color: "#c4c4cf", p: 0.5, "&.Mui-checked": { color: BRAND } }}
                      />

                      {/* Type icon */}
                      <Box
                        sx={{
                          flexShrink: 0, width: 40, height: 40, borderRadius: "11px",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: meta.color, backgroundColor: meta.bg,
                        }}
                      >
                        {meta.icon}
                      </Box>

                      {/* Text */}
                      <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => ReadNotifications(item)}>
                        <Typography
                          sx={{
                            fontSize: "14px", lineHeight: 1.45,
                            color: unread ? "#101828" : "#475467",
                            fontWeight: unread ? 600 : 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {item.Description.replace(/\d{4}-\d{2}-\d{2}/, (match) => moment(match).format("MM/DD/YYYY"))}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 13, color: "#98a2b3" }} />
                          <Typography sx={{ fontSize: "12px", color: "#98a2b3" }}>
                            {formatNotificationTime(item.Create_Time)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Right side: unread chip + open */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                        {unread && (
                          <Chip
                            size="small" label="New"
                            sx={{ height: 20, fontSize: "11px", fontWeight: 700, color: BRAND, backgroundColor: "#fdecec", border: `1px solid ${BRAND}33`, "& .MuiChip-label": { px: 0.9 } }}
                          />
                        )}
                        <Tooltip title="Open" arrow>
                          <Box
                            onClick={() => ReadNotifications(item)}
                            sx={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 32, height: 32, borderRadius: "8px", cursor: "pointer", color: "#98a2b3",
                              transition: "background-color 0.15s ease, color 0.15s ease",
                              "&:hover": { backgroundColor: "#fdecec", color: BRAND },
                            }}
                          >
                            <ChevronRightIcon sx={{ fontSize: 22 }} />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Pagination */}
              {totalPages > 1 && (
                <>
                  <Divider sx={{ mt: 2.5, mb: 1.5, borderColor: "#f1f1f5" }} />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      shape="rounded"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": { borderRadius: "8px", fontWeight: 600, color: "#475467" },
                        "& .Mui-selected": { backgroundColor: `${BRAND} !important`, color: "#fff" },
                        "& .MuiPaginationItem-root:hover": { backgroundColor: "#f4f4f6" },
                        "& .Mui-selected:hover": { backgroundColor: `${BRAND} !important` },
                      }}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Notifications;
